import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.3/js/sparta-events-utility";
/**
 * Utility focus-trap-utility
 * @description utility to keep user focus within a container
 * @copyright 2018-4-18
 * @file
 */
//  added as class to container element instance is invoked on
const utilName = 'focus-trap-utility';
const fontStyle = 'cnx-regular';

const EVENTS = {
  FOCUSOUT: `FOCUSOUT.${utilName}`,
};

/**
 * @function createBoundaryElement
 * @desc  makes a focuable div with given innerHTML
 * @param {string} htmlContent html content of the div to be created
 * @param {string} className class to be applied to the div to be created
 * @param {boolean} ariaHidden determines if screen readers can access boundary
 * @returns {node} focusable div
 */
const createBoundaryElement = (htmlContent, className, ariaHidden) => {
  const bound = document.createElement('div');
  bound.innerHTML = htmlContent;
  bound.classList.add(className);
  bound.classList.add('ada-hidden');
  bound.classList.add(fontStyle);
  bound.setAttribute('aria-hidden', !!ariaHidden);
  bound.setAttribute('tabindex', '-1');
  return bound;
};
// TODO: Joshua Smith
// research if these strings can move to data-transport
// TODO: Joshua Smith
// get these strings translated into Spanish
const englishBeginText = 'Beginning of Layer';
const spanishBeginText = 'Inicio de la ventana';
const englishEndText = 'End of Layer';
const spanishEndText = 'Fin de la ventana';

// capture correct language boundary text
const isSpanish = sparta.language === 'es'; // eslint-disable-line no-undef
const defaultBeginText = isSpanish ? spanishBeginText : englishBeginText;
const defaultEndText = isSpanish ? spanishEndText : englishEndText;

/**
 * @function FocusTrap
 * @desc  class to contain user focus navigation (tab/swipe) inside a given container
 * @returns {function} class to be instantiated
 */
class FocusTrap {
  /**
   * @function constructor
   * @desc  init instance specific util attributes
   * @param {object} args object to contain reference to container optional override html content
   * @returns {undefined} no explicit return
   */
  constructor(args = {}) {
    // text to be read when focus is moved
    this.layerBeginText = args.layerBeginText || defaultBeginText;
    this.layerEndText = args.layerEndText || defaultEndText;
    this.hideBoundText = !!args.hideBoundText || false;
    // container user will be confined to
    this.setContainer(args.container, args.containerID);
    this.setBounds();
    // attributes that will be set by attaching the instance to a container
    this.previousFocus = null;
    // prebind callbacks so they can be removed
    this.focusOut = this.focusoutCallback.bind(this);
    this.focusIn = this.focusinCallback.bind(this);
  }

  /**
   * @function setBounds
   * @desc Creates the elements that will be used as focus boundries
   * @param {string} layerBeginText text for the top boundry
   * @param {string} layerEndText text for the bottom boundry
   * @returns {undefined} no explicit return
   */
  setBounds(layerBeginText = this.layerBeginText, layerEndText = this.layerEndText) {
    this.topBound = this.addClickHandler(createBoundaryElement(layerBeginText, 'focus-boundary-top', this.hideBoundText));
    this.bottomBound = this.addClickHandler(createBoundaryElement(layerEndText, 'focus-boundary-bottom', this.hideBoundText));
    this.bounceBound = createBoundaryElement('', 'focus-boundary-bounce', true);
  }

  /**
   * @function setClasslist
   * @desc  safely sets the classList of the container
   * @param {string} containerClass (optional) string that will be added to the container
   * @param {node} container (optional) element to which the class will be added
   * @returns {instance} this instance of the focus trap
   */
  setClasslist(containerClass = utilName, container = this.container) {
    if (container && container.classList) {
      container.classList.add(containerClass);
    }
    return this;
  }

  /**
   * @function setContainer
   * @desc  resets the focus trap container without creating a new instance
   * @param {node} container dom node of container to attach trap to
   * @param {string} containerID id of the container to be used when the container is not available
   * @returns {instance} this instance of the focus trap
   */
  setContainer(container, containerID) {
    this.removeTrap();
    this.container = container || document.getElementById(containerID);
    this.setClasslist();
    return this;
  }

  /**
   * @function wasBounced
   * @desc  detects if the previous active element was a countainer boundary
   * @returns {Boolean} Boolean indicator of if the focus came from container boundary
   */
  wasBounced() {
    return this.previousFocus === this.bottomBound || this.previousFocus === this.topBound;
  }

  /**
   * @function getFocusoutTarget
   * @desc  checks where the focus should be sent after focusout fires on container boundary
   * @param {object} event DOM event from the focusout
   * @returns {node} container boundary which the focus is to be sent to
   */
  getFocusoutTarget(event) {
    return event.target === this.topBound ? this.bottomBound : this.topBound;
  }

  /**
   * @function focusoutCallback
   * @desc  checks if user is leaving container, and wrangles focus back to container
   * @param {event} event focusout DOM event
   * @returns {object} instance of FocusTrap class
   */
  focusoutCallback(event) {
    if (!this.wasBounced()) {
      const nextElem = event.relatedTarget || document.activeElement;
      if (!this.container.contains(nextElem)) {
        const targetElem = this.getFocusoutTarget(event);
        targetElem.focus();
        this.previousFocus = nextElem;
        spartaEventsUtility.pub(EVENTS.FOCUSOUT, {
          event,
        });
      }
    }
    return this;
  }

  /**
   * @function focusinCallback
   * @desc  bounces focus from bottommost bound to top
   * @returns {object} instance of FocusTrap class
   */
  focusinCallback() {
    this.topBound.focus();
    return this;
  }

  /**
   * Bind keydown events to focus trap via keyboard
   * @param {*} object 
   */
  modalClickEvent(object) {
    let container = object.container;
    let topBound = object.topBound;
    let bottomBound = object.bottomBound;
    object.container.addEventListener('keydown', function (event) {
      let thisKeyCode = event.charCode || event.keyCode;
      let trigger = event.target || event.srcElement;

      // get tabbable elements to cross reference
      let tabbables = container.querySelectorAll('button, [href], input, select, textarea');
      let tabbablesFirst = tabbables[0];
      let tabbablesLast = tabbables[tabbables.length - 1];

      // if shift + tab
      if (event.shiftKey && event.key === 'Tab')  {
        // if bottom trap is focused send focus to last tabbable item
        if (trigger === bottomBound) {
          event.preventDefault();
          tabbablesLast.focus();
        }
        // if top trap is focused send focus to bottom trap
        else if (trigger === topBound) {
          event.preventDefault();
          bottomBound.focus();
        }
      }
      // on tab press
      else if (thisKeyCode === 9) {
        // if last tabbable item is focused send focus to bottom trap
        if (trigger === tabbablesLast) {
          event.preventDefault();
          bottomBound.focus();
        }
        // if bottom trap is focused send focus to top trap
        else if (trigger === bottomBound) {
          event.preventDefault();
          topBound.focus();
        }
      }
    })
  }

  /**
   * @function modifyBoundEvents
   * @desc  attaches or removes event listeners on array of DOM nodes
   * @param {string} eventAction name of listener method
   * @returns {object} instance of FocusTrap class
   */
  modifyBoundEvents(eventAction) {
    this.container[eventAction]('focusout', this.focusOut, true);
    this.container[eventAction]('keydown', this.modalClickEvent(this), true);
    this.bounceBound[eventAction]('focusin', this.focusIn, true);
    return this;
  }

  /**
   * @function attachFocusListeners
   * @desc  leverages modifyBoundEvents method to add event listeners to nodes
   * @returns {object} instance of FocusTrap class (as returned by modifyBoundEvents)
   */
  attachFocusListeners() {
    return this.modifyBoundEvents('addEventListener');
  }

  /**
   * @function removeFocusListeners
   * @desc  leverages modifyBoundEvents method to add event listeners to nodes
   * @returns {object} instance of FocusTrap class (as returned by modifyBoundEvents)
   */
  removeFocusListeners() {
    return this.modifyBoundEvents('removeEventListener');
  }

  /**
   * @function clickHandler
   * @desc  description
   * @param {node} target paramDescription
   * @returns {object} instance of FocusTrap class
   */
  addClickHandler(target) {
    target.addEventListener('click', (event) => {
      this.getFocusoutTarget(event).focus();
    });
    return target;
  }

  /**
   * @function addBounds
   * @desc  creates, injects, and stores container boundaries
   * @param {string} layerBeginText (optional) DOM string injected into the upper container bound
   * @param {string} layerEndText (optional) DOM string injected into the lower container bound
   * @returns {object} instance of FocusTrap class
   */
  addBounds() {
    this.container.insertBefore(this.topBound, this.container.firstElementChild);
    this.container.appendChild(this.bottomBound);
    this.container.appendChild(this.bounceBound);

    return this;
  }

  /**
   * @function removeItem
   * @desc  node removal with existence check
   * @param {node} item item to be removed from the container
   * @param {node} container container item must be child of
   * @returns {object} this instance of the class
   */
  removeItem(item, container = this.container) {
    if (item && container.contains(item)) {
      container.removeChild(item);
    }
    return this;
  }

  /**
   * @function removeBounds
   * @param {node} topBound top focusable trap element to be removed
   * @param {node} bottomBound bottom focusable trap element to be removed
   * @desc  remove container boundaries from containers
   * @returns {object} instance of FocusTrap class
   */
  removeBounds() {
    const { topBound, bottomBound, bounceBound } = this;
    this.removeItem(topBound)
      .removeItem(bottomBound)
      .removeItem(bounceBound);
    return this;
  }

  /**
   * @function attachTrap
   * @desc  add classlist to container, attach event listeners, add boundary elements
   * @param {node} container can change the container the trap is attached to
   * @returns {object} instance of FocusTrap class
   */
  attachTrap() {
    this.setClasslist();
    this.addBounds();
    this.attachFocusListeners();
    this.container.setAttribute('tabindex', -1);
    return this;
  }

  /**
   * @function removeTrap
   * @param {string} containerClass (optional) class to add and remove from the focus container
   * @desc  remove util class from container, remove event listeners, delete boundary elements
   * @returns {object} instance of FocusTrap class
   */
  removeTrap(containerClass = utilName) {
    if (this.container) {
      this.container.classList.remove(containerClass);
      this.removeFocusListeners();
      this.removeBounds();
    }
    return this;
  }
}

const createFocusTrap = args => new FocusTrap(args);
createFocusTrap.EVENTS = EVENTS;
export default createFocusTrap;
